import { createMsgDeskFacade, type MsgDeskFacade } from '@/msgdeskRuntime/facade/MsgDeskFacade'
import type { MsgDeskInstalledApp, MsgDeskParams } from '@/msgdeskRuntime/types'

type BrowserWindow = Window & typeof globalThis

export interface LegacyRuntimeGlobals {
  window?: Window
  document?: Document
  realWindow?: Window
  realDocument?: Document
  $?: unknown
  jQuery?: unknown
  plugins?: unknown
  virtual?: boolean
}

export interface MsgDeskRuntimeDiagnostics {
  legacyRuntimeInstalled: boolean
  selectorAliasesInstalled: boolean
  strictVirtualDom: boolean
  fallbackCount: number
  registryKeys: string[]
}

declare global {
  interface Window {
    __MSG_PARAMS__?: MsgDeskParams
    SET_IS_TEST_MODE?: boolean
    __MSG_ALERT_HOOK__?: (message: string) => void
    __MSG_ALERTS__?: string[]
    __MSG_EVENT_BUS__?: { emit: (eventName: string, ...args: any[]) => void }
    msgApp?: MsgDeskInstalledApp
    __MSG_APP__?: MsgDeskInstalledApp
    __MSG_LEGACY_SELECTOR_ALIASES__?: unknown
    __MSG_V2_STRICT_VIRTUAL_DOM__?: boolean
    __MSG_V2_FALLBACKS__?: unknown[]
    __MSGDESK_DEBUG__?: unknown
  }
}

const DEBUG_GLOBAL_ALLOWLIST = new Set([
  'msgObj',
  'treeStore',
  'MSG_OBJ',
  'NEW_MSG_OBJ',
  'MSG_UTILS',
  'msgdsk',
  'dsk',
  '__MSG_APP__',
  'msgApp',
  '__MSG_EVENT_BUS__',
  '__MSG_V2_STRICT_VIRTUAL_DOM__',
  '__MSG_V2_FALLBACKS__',
  '__MSG_LAST_PROJECTION_SYNC__',
  '__MSG_LAST_MOVE_PROJECTION_SYNC__',
  '__MSG_LEGACY_OPERATION_GATE_STATE__',
  '__MSG_LEGACY_LIFECYCLE_TRACE__',
])

function boolParam(value: string | null): boolean | undefined {
  if (value === null) return undefined
  if (value === '' || value === '1' || value === 'true') return true
  if (value === '0' || value === 'false') return false
  return true
}

function boolValue(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return boolParam(value)
  return undefined
}

function currentBrowserWindow(): BrowserWindow | undefined {
  return typeof window === 'undefined' ? undefined : window
}

export class MsgDeskRuntime {
  readonly surface: string
  readonly registry = new Map<string, unknown>()
  private facadeInstance: MsgDeskFacade | null = null
  private legacyRuntimeInstance: LegacyRuntimeGlobals | null = null

  constructor(surface = 'template2') {
    this.surface = surface
  }

  get facade(): MsgDeskFacade {
    if (!this.facadeInstance) this.facadeInstance = createMsgDeskFacade(this)
    return this.facadeInstance
  }

  get browserWindow(): BrowserWindow | undefined {
    return currentBrowserWindow()
  }

  get document(): Document | undefined {
    return this.browserWindow?.document
  }

  get body(): HTMLElement | undefined {
    return this.document?.body
  }

  get location(): Location | undefined {
    return this.browserWindow?.location
  }

  get title(): string {
    return this.document?.title || ''
  }

  get params(): MsgDeskParams | undefined {
    return this.getGlobal<MsgDeskParams>('__MSG_PARAMS__')
  }

  setParams(params: MsgDeskParams) {
    this.setGlobal('__MSG_PARAMS__', params)
  }

  readParamsFromPageData(): (MsgDeskParams & { is_test_mode?: boolean }) | undefined {
    const raw = this.document?.getElementById('msgdesk-params')?.textContent?.trim()
    if (!raw) return undefined
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const codekey = typeof parsed.codekey === 'string' ? parsed.codekey : ''
      if (!codekey) return undefined
      return {
        codekey,
        keyname: typeof parsed.keyname === 'string' ? parsed.keyname : undefined,
        show_pin: typeof parsed.show_pin === 'string' ? parsed.show_pin : undefined,
        is_link_view: boolValue(parsed.is_link_view),
        is_edit_mode: boolValue(parsed.is_edit_mode),
        is_test_mode: boolValue(parsed.is_test_mode),
      }
    } catch {
      return undefined
    }
  }

  ensureParamsFromUrl() {
    const win = this.browserWindow
    if (!win || this.params?.codekey) return

    const pageParams = this.readParamsFromPageData()
    if (pageParams?.codekey) {
      const { is_test_mode: isTestMode, ...params } = pageParams
      this.setParams(params)
      if (isTestMode) this.setGlobal('SET_IS_TEST_MODE', true)
      this.installDebugGlobalIfNeeded()
      return
    }

    const params = new URLSearchParams(win.location.search)
    const codekey = params.get('codekey') || params.get('key') || ''
    const showPin = params.get('show_pin') || params.get('showpin') || undefined
    const keyname = params.has('key') && !params.has('codekey') ? 'key' : undefined

    this.setParams({
      codekey,
      keyname,
      show_pin: showPin,
      is_link_view: boolParam(params.get('is_link_view')),
      is_edit_mode: boolParam(params.get('is_edit_mode')),
    })

    if (params.has('test')) this.setGlobal('SET_IS_TEST_MODE', true)
    this.installDebugGlobalIfNeeded()
  }

  installAlertHook() {
    if (typeof this.getGlobal('__MSG_ALERT_HOOK__') === 'function') return
    const alerts = this.ensureArrayGlobal<string>('__MSG_ALERTS__')
    this.setGlobal('__MSG_ALERT_HOOK__', (message: string) => {
      alerts.push(message)
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[legacy alert]', message)
      }
    })
  }

  installCompatibilityApp<T extends MsgDeskInstalledApp>(app: T): T {
    this.setGlobal('msgApp', app)
    this.setGlobal('__MSG_APP__', app)
    return app
  }

  getInstalledApp<T extends MsgDeskInstalledApp = MsgDeskInstalledApp>(): T | undefined {
    return (this.getGlobal('msgApp') || this.getGlobal('__MSG_APP__')) as T | undefined
  }

  get eventBus() {
    return this.getGlobal<{ emit: (eventName: string, ...args: any[]) => void }>('__MSG_EVENT_BUS__') || { emit: () => undefined }
  }

  async boot(): Promise<LegacyRuntimeGlobals | null> {
    if (this.legacyRuntimeInstance) return this.legacyRuntimeInstance
    // Default runtime is the 1:1 TypeScript port (msgdeskRuntime/legacy_ts),
    // equivalence-proven against the generated bundle on 2026-07-03
    // (scripts/check-legacy-ts-equivalence.mjs + behavioral differentials).
    // Rollback: NEXT_PUBLIC_MSGDESK_LEGACY_JS=1 boots the old generated
    // bundle, kept as the frozen reference. Both export the same
    // createLegacyRuntime contract.
    const legacyModule = process.env.NEXT_PUBLIC_MSGDESK_LEGACY_JS === '1'
      ? await import('@/msgdeskRuntime/legacy/legacy-react-bundle.generated.js')
      : await import('@/msgdeskRuntime/legacy_ts')
    const createLegacyRuntime =
      (legacyModule as any).createLegacyRuntime ||
      (legacyModule as any).default?.createLegacyRuntime ||
      (legacyModule as any).default

    if (typeof createLegacyRuntime !== 'function') {
      throw new Error('MsgDesk legacy runtime factory is not available')
    }

    this.legacyRuntimeInstance = createLegacyRuntime() || null

    // S1 island inversion: React hooks/bridges (e.g. __MSG_REACT_SUBS__ from
    // bindDataTreeGlobalHooks, __MSG_REACT_UI__) may now be installed BEFORE
    // the legacy runtime boots — setGlobal stored them in the registry, but
    // the legacy window did not exist yet and legacy code reads its OWN
    // window. Replay the registry onto the freshly created legacy window so
    // pre-boot installs behave exactly like post-boot ones.
    const legacyWindow = this.legacyRuntimeInstance?.window as any
    if (legacyWindow) {
      for (const [name, value] of this.registry) {
        legacyWindow[name] = value
      }
    }

    return this.legacyRuntimeInstance
  }

  get legacyRuntime(): LegacyRuntimeGlobals | null {
    return this.legacyRuntimeInstance
  }

  get legacyWindow(): any {
    return this.legacyRuntime?.window || this.browserWindow
  }

  get realWindow(): BrowserWindow | undefined {
    return this.browserWindow || (this.legacyRuntime?.realWindow as BrowserWindow | undefined)
  }

  getLegacyObject<T = any>(name: string): T | undefined {
    if (this.registry.has(name)) return this.registry.get(name) as T
    return (this.legacyWindow as any)?.[name] ?? (this.realWindow as any)?.[name]
  }

  get legacyUtils(): any {
    return this.facade.utils
  }

  getGlobal<T = any>(name: string): T | undefined {
    if (this.registry.has(name)) return this.registry.get(name) as T
    const legacyWindow = this.legacyRuntime?.window as any
    if (legacyWindow && name in legacyWindow) return legacyWindow[name] as T
    return (this.realWindow as any)?.[name]
  }

  setGlobal<T>(name: string, value: T): T {
    this.registry.set(name, value)
    const legacyWindow = this.legacyRuntime?.window as any
    if (legacyWindow) legacyWindow[name] = value
    if (this.shouldExposeDebugGlobal(name)) {
      const win = this.realWindow as any
      if (win) win[name] = value
    }
    return value
  }

  deleteGlobal(name: string) {
    this.registry.delete(name)
    const legacyWindow = this.legacyRuntime?.window as any
    if (legacyWindow) delete legacyWindow[name]
    if (this.shouldExposeDebugGlobal(name)) {
      const win = this.realWindow as any
      if (win) delete win[name]
    }
  }

  ensureArrayGlobal<T = unknown>(name: string): T[] {
    const existing = this.getGlobal<T[]>(name)
    if (Array.isArray(existing)) return existing
    return this.setGlobal(name, [] as T[])
  }

  installEventBus(emit: (eventName: string, ...args: any[]) => unknown) {
    return this.setGlobal('__MSG_EVENT_BUS__', { emit })
  }

  installReactUiFacade(facade: Record<string, unknown>) {
    return this.setGlobal('__MSG_REACT_UI__', facade)
  }

  getReactUiFacade<T = Record<string, unknown>>(): T | undefined {
    return this.getGlobal<T>('__MSG_REACT_UI__')
  }

  recordFallback(detail: unknown): unknown[] {
    const fallbacks = this.ensureArrayGlobal<unknown>('__MSG_V2_FALLBACKS__')
    fallbacks.push(detail)
    return fallbacks
  }

  isStrictVirtualDom(): boolean {
    return this.getGlobal('__MSG_V2_STRICT_VIRTUAL_DOM__') === true
  }

  private debugGlobalsEnabled(): boolean {
    const location = this.location
    if (!location) return false
    const params = new URLSearchParams(location.search)
    return params.has('debug_runtime') || params.has('debug_globals')
  }

  private shouldExposeDebugGlobal(name: string): boolean {
    return this.debugGlobalsEnabled() && DEBUG_GLOBAL_ALLOWLIST.has(name)
  }

  installDebugGlobalIfNeeded() {
    const win = this.browserWindow
    if (!win || !this.debugGlobalsEnabled() || win.__MSGDESK_DEBUG__) return
    win.__MSGDESK_DEBUG__ = {
      runtime: this,
      get: (key: string) => this.getGlobal(key),
      keys: () => Array.from(this.registry.keys()),
      snapshot: () => this.diagnostics(),
    }
  }

  querySelector<T extends Element = Element>(selector: string): T | null {
    return this.document?.querySelector(selector) as T | null
  }

  querySelectorAll<T extends Element = Element>(selector: string): T[] {
    const doc = this.document
    if (!doc) return []
    return Array.from(doc.querySelectorAll(selector)) as T[]
  }

  getElementById<T extends HTMLElement = HTMLElement>(id: string): T | null {
    return this.document?.getElementById(id) as T | null
  }

  createElement<K extends keyof HTMLElementTagNameMap>(tagName: K): HTMLElementTagNameMap[K] | null {
    return this.document?.createElement(tagName) || null
  }

  appendToHead(node: HTMLElement): boolean {
    const head = this.document?.head
    if (!head) return false
    head.appendChild(node)
    return true
  }

  addDocumentEventListener<K extends keyof DocumentEventMap>(
    name: K,
    handler: (event: DocumentEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ) {
    this.document?.addEventListener(name, handler, options)
  }

  removeDocumentEventListener<K extends keyof DocumentEventMap>(
    name: K,
    handler: (event: DocumentEventMap[K]) => void,
    options?: boolean | EventListenerOptions,
  ) {
    this.document?.removeEventListener(name, handler, options)
  }

  dispatchCustomEvent<T = unknown>(name: string, detail?: T): boolean {
    const win = this.browserWindow
    if (!win) return false
    return win.dispatchEvent(new CustomEvent(name, detail === undefined ? undefined : { detail }))
  }

  addEventListener(name: string, handler: EventListenerOrEventListenerObject) {
    this.browserWindow?.addEventListener(name, handler)
  }

  removeEventListener(name: string, handler: EventListenerOrEventListenerObject) {
    this.browserWindow?.removeEventListener(name, handler)
  }

  setTimeout(handler: () => void, timeout = 0): number {
    return this.browserWindow?.setTimeout(handler, timeout) ?? 0
  }

  clearTimeout(id: number) {
    if (id) this.browserWindow?.clearTimeout(id)
  }

  createObjectURL(file: Blob): string {
    return this.browserWindow?.URL?.createObjectURL(file) || ''
  }

  isTestMode(): boolean {
    return this.getGlobal('SET_IS_TEST_MODE') !== undefined
  }

  redirectToLegacyLink(html: string | null | undefined) {
    const win = this.browserWindow
    if (!win) return
    win.location.href = html ? html.slice(8) : '/'
  }

  diagnostics(): MsgDeskRuntimeDiagnostics {
    return {
      legacyRuntimeInstalled: Boolean(this.legacyRuntime),
      selectorAliasesInstalled: Boolean(this.getGlobal('__MSG_LEGACY_SELECTOR_ALIASES__')),
      strictVirtualDom: this.isStrictVirtualDom(),
      fallbackCount: Array.isArray(this.getGlobal('__MSG_V2_FALLBACKS__')) ? this.getGlobal<unknown[]>('__MSG_V2_FALLBACKS__')!.length : 0,
      registryKeys: Array.from(this.registry.keys()),
    }
  }
}

let runtime: MsgDeskRuntime | null = null

export function getMsgDeskRuntime(surface = 'template2'): MsgDeskRuntime {
  if (!runtime || runtime.surface !== surface) runtime = new MsgDeskRuntime(surface)
  return runtime
}
